import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Trash2, Info, HelpCircle } from 'lucide-react';

/**
 * Custom confirmation modal to replace browser alerts/confirms
 *
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Called when modal is dismissed (backdrop click, cancel, X)
 * @param {function} onConfirm - Called when confirm button is clicked
 * @param {string} title - Modal title
 * @param {string} message - Modal message/description
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - Visual style: "danger" | "warning" | "info" (default: "danger")
 * @param {boolean} showCancel - Whether to show cancel button (default: true)
 */
export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  showCancel = true
}) {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // Variant styles
  const variantStyles = {
    danger: {
      icon: <Trash2 size={24} />,
      iconColor: 'text-red-600',
      confirmBtn: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warning: {
      icon: <AlertTriangle size={24} />,
      iconColor: 'text-yellow-600',
      confirmBtn: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    },
    info: {
      icon: <Info size={24} />,
      iconColor: 'text-blue-600',
      confirmBtn: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
    question: {
      icon: <HelpCircle size={24} />,
      iconColor: 'text-purple-600',
      confirmBtn: 'bg-purple-600 hover:bg-purple-700 text-white',
    },
  };

  const styles = variantStyles[variant] || variantStyles.danger;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly"
      onClick={handleBackdropClick}
      aria-modal="true"
      role="alertdialog"
    >
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 animate-slideUp relative">
        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className={`absolute top-5 right-5 ${styles.iconColor}`}>
            {styles.icon}
          </div>

          {/* Text */}
          <div className="text-left">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {title}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {message}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3 justify-end">
          {showCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-slate-600 bg-white/80 hover:bg-slate-50 border border-slate-200 rounded-xl transition-all"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-xs font-medium rounded-xl transition-all ${styles.confirmBtn}`}
          >
            {confirmText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.2s ease-out;
        }
      `}</style>
    </div>,
    document.body
  );
}

/**
 * Hook to use the confirm modal imperatively
 *
 * Usage:
 * const { showConfirm, ConfirmModalComponent } = useConfirmModal();
 *
 * // In your handler:
 * const confirmed = await showConfirm({
 *   title: 'Delete palette?',
 *   message: 'This action cannot be undone.',
 *   confirmText: 'Delete',
 *   variant: 'danger'
 * });
 * if (confirmed) { ... }
 *
 * // In your JSX:
 * return <>{ConfirmModalComponent}</>
 */
export function useConfirmModal() {
  const [modalState, setModalState] = React.useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'danger',
    showCancel: true,
    resolve: null,
  });

  const showConfirm = React.useCallback((options) => {
    return new Promise((resolve) => {
      setModalState({
        isOpen: true,
        title: options.title || 'Confirm',
        message: options.message || 'Are you sure?',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'danger',
        showCancel: options.showCancel !== false,
        resolve,
      });
    });
  }, []);

  const handleClose = React.useCallback(() => {
    if (modalState.resolve) {
      modalState.resolve(false);
    }
    setModalState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [modalState.resolve]);

  const handleConfirm = React.useCallback(() => {
    if (modalState.resolve) {
      modalState.resolve(true);
    }
    setModalState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [modalState.resolve]);

  const ConfirmModalComponent = (
    <ConfirmModal
      isOpen={modalState.isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={modalState.title}
      message={modalState.message}
      confirmText={modalState.confirmText}
      cancelText={modalState.cancelText}
      variant={modalState.variant}
      showCancel={modalState.showCancel}
    />
  );

  return { showConfirm, ConfirmModalComponent };
}
